import { canManageOrganization } from 'util/organizationAccess';
import React, { useMemo, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import corner6 from 'assets/img/illustrations/corner-6.png';
import AdminDashboardDateRange from './components/AdminDashboardDateRange';
import AdminDashboardConnectionRequestsCard from './components/AdminDashboardConnectionRequestsCard';
import AdminDashboardMetricCard from './components/AdminDashboardMetricCard';
import AdminBillingDashboard from './AdminBillingDashboard';
import {
  getDefaultDashboardDateRange,
  graphToChartData,
  toDashboardQueryParams
} from './components/adminDashboardUtils';
import {
  useGetDashboardConnectionRequestsQuery,
  useGetDashboardJournalsQuery,
  useGetDashboardLocationsQuery,
  useGetDashboardNotarizationsQuery,
  useGetDashboardNotariesQuery
} from 'rtk/org-admin/tenantDashboard.slice';
import { useListTenantLocationsQuery } from 'rtk/org-admin/location.slice';

const AdminDashboard = () => {
  const canManage = useSelector(state =>
    canManageOrganization(state.authentication?.userRoles)
  );
  const [dateRange, setDateRange] = useState(getDefaultDashboardDateRange);
  const [selectedLocationGuid, setSelectedLocationGuid] = useState('');
  const userMaster = useSelector(state => state.app.userMaster);
  const tenantGuid = userMaster?.TenantGUID || null;

  const {
    data: tenantLocationsRes,
    isFetching: tenantLocationsFetching,
    isLoading: tenantLocationsLoading
  } = useListTenantLocationsQuery(
    {
      tenantGuid,
      indexStart: 0,
      maxResults: 1000
    },
    { skip: !tenantGuid }
  );

  const locationOptions = useMemo(() => {
    const objects = Array.isArray(tenantLocationsRes?.Objects)
      ? tenantLocationsRes.Objects
      : [];

    return objects
      .filter(location => location?.GUID)
      .map(location => ({
        guid: location.GUID,
        label: location.DisplayName || location.GUID
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [tenantLocationsRes]);

  const queryParams = useMemo(
    () => toDashboardQueryParams(dateRange, selectedLocationGuid),
    [dateRange, selectedLocationGuid]
  );

  const {
    data: notariesData,
    isFetching: notariesFetching,
    isLoading: notariesLoading,
    error: notariesError,
    refetch: refetchNotaries
  } = useGetDashboardNotariesQuery(queryParams);

  const {
    data: journalsData,
    isFetching: journalsFetching,
    isLoading: journalsLoading,
    error: journalsError,
    refetch: refetchJournals
  } = useGetDashboardJournalsQuery(queryParams);

  const {
    data: notarizationsData,
    isFetching: notarizationsFetching,
    isLoading: notarizationsLoading,
    error: notarizationsError,
    refetch: refetchNotarizations
  } = useGetDashboardNotarizationsQuery(queryParams);

  const {
    data: locationsData,
    isFetching: locationsFetching,
    isLoading: locationsLoading,
    error: locationsError,
    refetch: refetchLocations
  } = useGetDashboardLocationsQuery();

  const {
    data: connectionRequestsData,
    isFetching: connectionRequestsFetching,
    isLoading: connectionRequestsLoading,
    error: connectionRequestsError,
    refetch: refetchConnectionRequests
  } = useGetDashboardConnectionRequestsQuery(queryParams);

  const notariesChartData = useMemo(
    () => graphToChartData(notariesData?.Graph),
    [notariesData]
  );

  const journalsChartData = useMemo(
    () => graphToChartData(journalsData?.Graph),
    [journalsData]
  );

  const notarizationsChartData = useMemo(
    () => graphToChartData(notarizationsData?.Graph),
    [notarizationsData]
  );

  const locationsChartData = useMemo(
    () => graphToChartData(locationsData?.Graph),
    [locationsData]
  );

  return (
    <Row className="mb-3 g-3">
      <Col lg={12}>
        <FalconComponentCard noGuttersBottom fullHeight>
          <FalconComponentCard.Header
            loading={
              notariesFetching ||
              notariesLoading ||
              journalsFetching ||
              journalsLoading ||
              notarizationsFetching ||
              notarizationsLoading ||
              locationsFetching ||
              locationsLoading ||
              connectionRequestsFetching ||
              connectionRequestsLoading
            }
            onRefresh={() => {
              refetchNotaries();
              refetchJournals();
              refetchNotarizations();
              refetchLocations();
              refetchConnectionRequests();
            }}
            title="Dashboard"
            subtitle="Organization metrics for notaries, journals, notarizations, locations, and connection requests."
            image={corner6}
          />
          <FalconComponentCard.Body noLight className="py-3">
            <div className="mb-3 pb-2 border-bottom">
              <Flex alignItems="center" className="flex-wrap gap-3">
                <AdminDashboardDateRange
                  value={dateRange}
                  onChange={setDateRange}
                />
                <Form.Group className="mb-0" style={{ minWidth: '220px' }}>
                  <Form.Label className="visually-hidden">Location</Form.Label>
                  <Form.Select
                    size="sm"
                    value={selectedLocationGuid}
                    onChange={event =>
                      setSelectedLocationGuid(event.target.value)
                    }
                    disabled={tenantLocationsFetching || tenantLocationsLoading}
                    aria-label="Filter by location"
                  >
                    <option value="">All locations</option>
                    {locationOptions.map(location => (
                      <option key={location.guid} value={location.guid}>
                        {location.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Flex>
            </div>
            <Row className="g-3">
              <Col xs={6} lg={3}>
                <AdminDashboardMetricCard
                  title="Notaries"
                  totalCount={notariesData?.TotalCount}
                  chartData={notariesChartData}
                  loading={notariesFetching || notariesLoading}
                  error={notariesError}
                />
              </Col>
              <Col xs={6} lg={3}>
                <AdminDashboardMetricCard
                  title="Journal Entries"
                  totalCount={journalsData?.TotalCount}
                  chartData={journalsChartData}
                  loading={journalsFetching || journalsLoading}
                  error={journalsError}
                />
              </Col>
              <Col xs={6} lg={3}>
                <AdminDashboardMetricCard
                  title="Notarizations"
                  totalCount={notarizationsData?.TotalCount}
                  chartData={notarizationsChartData}
                  loading={notarizationsFetching || notarizationsLoading}
                  error={notarizationsError}
                />
              </Col>
              <Col xs={6} lg={3}>
                <AdminDashboardMetricCard
                  title="Locations"
                  totalCount={locationsData?.TotalCount}
                  chartData={locationsChartData}
                  loading={locationsFetching || locationsLoading}
                  error={locationsError}
                />
              </Col>
              <Col xs={6} lg={3}>
                <AdminDashboardConnectionRequestsCard
                  data={connectionRequestsData}
                  loading={
                    connectionRequestsFetching || connectionRequestsLoading
                  }
                  error={connectionRequestsError}
                />
              </Col>
            </Row>
          </FalconComponentCard.Body>
        </FalconComponentCard>
      </Col>
      {canManage && (
        <Col lg={12}>
          <section
            id="organization-billing"
            className="py-0"
            aria-label="Organization billing"
          >
            <AdminBillingDashboard dateRange={dateRange} />
          </section>
        </Col>
      )}
    </Row>
  );
};

export default AdminDashboard;
